import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import API from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { FolderKanban, FileText, BookOpen, TrendingUp, Calendar, ArrowRight, BarChart3, Trophy, XCircle, Globe, ShoppingCart, FileSpreadsheet, GripVertical, Eye, EyeOff, Settings2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Legend, LineChart, Line, CartesianGrid } from 'recharts';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';

const moduleLabels = { ofertant: 'Ofertant', autoritate: 'Autoritate', privat: 'Privat' };
const moduleColors = { ofertant: 'bg-blue-500/10 text-blue-400 ring-blue-500/20', autoritate: 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/20', privat: 'bg-amber-500/10 text-amber-400 ring-amber-500/20' };
const CHART_COLORS = ['#2563EB', '#06b6d4', '#10b981', '#f59e0b', '#f43f5e', '#8b5cf6'];
const STAT_ICONS = { FolderKanban, ShoppingCart, FileText, Trophy, FileSpreadsheet, Globe, TrendingUp, BookOpen, XCircle };

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return <div className="bg-zinc-900 border border-white/10 rounded-lg p-2 text-xs shadow-xl">{payload.map((p, i) => <div key={i} className="text-zinc-300"><span className="font-medium" style={{ color: p.color }}>{p.name || label}: </span>{p.value}</div>)}</div>;
};

// Default widget order per module
const defaultWidgets = {
  ofertant: ['stats', 'scores', 'procTypes', 'trends', 'deadlines', 'projects'],
  autoritate: ['stats', 'procTypes', 'trends', 'deadlines', 'projects'],
  privat: ['stats', 'scores', 'procTypes', 'trends', 'deadlines', 'projects'],
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [deadlines, setDeadlines] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const userType = user?.user_type || 'ofertant';
  const isAdmin = user?.is_admin || false;

  // Drag & drop state
  const storageKey = `sinsol_widgets_${userType}`;
  const [widgets, setWidgets] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey)) || defaultWidgets[userType]; }
    catch { return defaultWidgets[userType]; }
  });
  const [hiddenWidgets, setHiddenWidgets] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`${storageKey}_hidden`)) || []; }
    catch { return []; }
  });
  const [draggedWidget, setDraggedWidget] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    Promise.all([API.get('/analytics/overview'), API.get('/deadlines'), API.get('/projects')])
      .then(([a, d, p]) => { setAnalytics(a.data); setDeadlines(d.data.filter(dl => !dl.completed).slice(0, 5)); setProjects(p.data.slice(0, 5)); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(widgets));
    localStorage.setItem(`${storageKey}_hidden`, JSON.stringify(hiddenWidgets));
  }, [widgets, hiddenWidgets, storageKey]);

  const handleDragStart = (e, widgetId) => { setDraggedWidget(widgetId); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = (e, targetId) => {
    e.preventDefault();
    if (!draggedWidget || draggedWidget === targetId) return;
    const newWidgets = [...widgets];
    const fromIdx = newWidgets.indexOf(draggedWidget);
    const toIdx = newWidgets.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    newWidgets.splice(fromIdx, 1);
    newWidgets.splice(toIdx, 0, draggedWidget);
    setWidgets(newWidgets);
    setDraggedWidget(null);
    toast.success('Widget mutat');
  };

  const toggleWidget = (id) => {
    if (hiddenWidgets.includes(id)) {
      setHiddenWidgets(hiddenWidgets.filter(w => w !== id));
    } else {
      setHiddenWidgets([...hiddenWidgets, id]);
    }
  };

  if (loading) return <div className="p-8 space-y-4">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-white/5 animate-pulse rounded-lg" />)}</div>;

  const visibleWidgets = widgets.filter(w => !hiddenWidgets.includes(w));

  const widgetNames = {
    stats: 'Statistici', scores: 'Distributie Scoruri', procTypes: 'Tipuri Proceduri',
    trends: 'Trend Lunar', deadlines: 'Termene', projects: 'Proiecte Recente'
  };

  // Stat cards differ per module
  const renderStats = () => {
    if (userType === 'autoritate') {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard label="Proceduri Active" value={analytics?.projects_active || 0} sub={`din ${analytics?.projects_total || 0}`} icon="FolderKanban" color="text-blue-400" />
          <StatCard label="Achizitii Directe" value={analytics?.achizitii_directe || 0} icon="ShoppingCart" color="text-emerald-400" />
          <StatCard label="Documente" value={analytics?.documents_total || 0} icon="FileText" color="text-cyan-400" />
          <StatCard label="Finalizate" value={analytics?.projects_won || 0} icon="Trophy" color="text-amber-400" />
          <StatCard label="Formulare" value={analytics?.forms_generated || 0} icon="FileSpreadsheet" color="text-primary" />
        </div>
      );
    }
    if (userType === 'privat') {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard label="RFP/RFQ Active" value={analytics?.projects_active || 0} sub={`din ${analytics?.projects_total || 0}`} icon="Globe" color="text-blue-400" />
          <StatCard label="Finalizate" value={analytics?.projects_won || 0} icon="Trophy" color="text-emerald-400" />
          <StatCard label="Documente" value={analytics?.documents_total || 0} icon="FileText" color="text-cyan-400" />
          <StatCard label="Scor Mediu" value={`${analytics?.avg_win_score || 0}%`} icon="TrendingUp" color="text-amber-400" />
          <StatCard label="Raspunsuri" value={analytics?.responses_total || 0} icon="BookOpen" color="text-primary" />
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Licitatii Active" value={analytics?.projects_active || 0} sub={`din ${analytics?.projects_total || 0}`} icon="FolderKanban" color="text-blue-400" />
        <StatCard label="Castigate" value={analytics?.projects_won || 0} icon="Trophy" color="text-emerald-400" />
        <StatCard label="Pierdute" value={analytics?.projects_lost || 0} icon="XCircle" color="text-rose-400" />
        <StatCard label="Scor Mediu" value={`${analytics?.avg_win_score || 0}%`} icon="TrendingUp" color="text-amber-400" />
        <StatCard label="Formulare" value={analytics?.forms_generated || 0} icon="FileSpreadsheet" color="text-cyan-400" />
      </div>
    );
  };

  const widgetComponents = {
    stats: renderStats(),
    scores: analytics?.score_distribution && (
      <Card className="bg-card border-white/10"><CardHeader className="pb-2"><CardTitle className="text-sm" style={{ fontFamily: 'Outfit' }}><BarChart3 className="w-4 h-4 text-primary inline mr-2" />Distributie Scoruri</CardTitle></CardHeader>
        <CardContent><ResponsiveContainer width="100%" height={180}><BarChart data={analytics.score_distribution}><XAxis dataKey="range" tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} /><YAxis tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} /><Tooltip content={<ChartTooltip />} /><Bar dataKey="count" fill="#2563EB" radius={[4, 4, 0, 0]} name="Proiecte" /></BarChart></ResponsiveContainer></CardContent></Card>
    ),
    procTypes: analytics?.procurement_distribution?.length > 0 && (
      <Card className="bg-card border-white/10"><CardHeader className="pb-2"><CardTitle className="text-sm" style={{ fontFamily: 'Outfit' }}>{userType === 'autoritate' ? 'Tipuri Proceduri' : 'Tip Procedura'}</CardTitle></CardHeader>
        <CardContent><ResponsiveContainer width="100%" height={180}><PieChart><Pie data={analytics.procurement_distribution.map((d, i) => ({ ...d, fill: CHART_COLORS[i % CHART_COLORS.length] }))} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={70} label={false} /><Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" wrapperStyle={{ fontSize: 10, maxWidth: 140 }} formatter={(v) => (v || '').replace(/_/g, ' ')} /><Tooltip content={<ChartTooltip />} /></PieChart></ResponsiveContainer></CardContent></Card>
    ),
    trends: analytics?.monthly_trends?.length > 0 && (
      <Card className="bg-card border-white/10"><CardHeader className="pb-2"><CardTitle className="text-sm" style={{ fontFamily: 'Outfit' }}>Trend Lunar</CardTitle></CardHeader>
        <CardContent><ResponsiveContainer width="100%" height={180}><LineChart data={analytics.monthly_trends}><CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" /><XAxis dataKey="month" tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} /><YAxis tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} /><Tooltip content={<ChartTooltip />} /><Line type="monotone" dataKey="total" stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} name="Total" /><Line type="monotone" dataKey="castigat" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} name="Finalizate" /></LineChart></ResponsiveContainer></CardContent></Card>
    ),
    deadlines: (
      <Card className="bg-card border-white/10"><CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Calendar className="w-4 h-4 text-amber-400" strokeWidth={1.5} />Termene</CardTitle></CardHeader>
        <CardContent>{deadlines.length > 0 ? deadlines.map((dl, i) => (
          <div key={i} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
            <div className={`w-2 h-2 rounded-full shrink-0 ${dl.priority === 'high' ? 'bg-rose-400' : 'bg-amber-400'}`} />
            <p className="text-sm flex-1 truncate">{dl.title}</p>
            <span className="text-xs font-mono-data text-amber-400 shrink-0">{dl.date}</span>
          </div>
        )) : <p className="text-sm text-muted-foreground py-4 text-center">Niciun termen</p>}</CardContent></Card>
    ),
    projects: (
      <Card className="bg-card border-white/10"><CardHeader className="pb-3"><div className="flex items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><FolderKanban className="w-4 h-4 text-blue-400" strokeWidth={1.5} />{userType === 'autoritate' ? 'Proceduri Recente' : userType === 'privat' ? 'RFP-uri Recente' : 'Proiecte Recente'}</CardTitle>
        <Link to="/projects" className="text-xs text-primary hover:underline flex items-center gap-1">Vezi toate <ArrowRight className="w-3 h-3" /></Link>
      </div></CardHeader>
        <CardContent>{projects.length > 0 ? projects.map((proj, i) => (
          <Link key={i} to={`/projects/${proj.id}`} className="flex items-center gap-3 py-2 px-2 -mx-2 border-b border-white/5 last:border-0 hover:bg-white/5 rounded-md transition-colors">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{proj.name}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className={`text-[10px] ${moduleColors[proj.module_type] || ''} ring-1 ring-inset border-0`}>{moduleLabels[proj.module_type]}</Badge>
                <StatusBadge status={proj.status} />
              </div>
            </div>
            {proj.win_score > 0 && <p className="text-sm font-bold text-emerald-400 font-mono-data">{proj.win_score}%</p>}
          </Link>
        )) : <p className="text-sm text-muted-foreground py-4 text-center">Niciun proiect</p>}</CardContent></Card>
    ),
  };

  return (
    <div className="p-8" data-testid="dashboard-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>Bun venit, {user?.name}</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {isAdmin ? 'SAGA Infrastructura - BiddingAssistant - Admin Dashboard' :
             userType === 'autoritate' ? 'SAGA Infrastructura - BiddingAssistant - Autoritate Contractanta' :
             userType === 'privat' ? 'SAGA Infrastructura - BiddingAssistant - Achizitii Private' :
             'SAGA Infrastructura - BiddingAssistant - Drumuri & Asfaltari'}
          </p>
          {isAdmin && <Badge className="mt-2 bg-amber-500/10 text-amber-400 text-[10px]">Admin - Vizualizare Globala</Badge>}
        </div>
        <Popover open={showSettings} onOpenChange={setShowSettings}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" data-testid="dashboard-settings-btn">
              <Settings2 className="w-3.5 h-3.5 mr-1.5" />Personalizare
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 bg-zinc-950 border-white/10 p-3" align="end">
            <p className="text-xs font-semibold mb-3" style={{ fontFamily: 'Outfit' }}>Widgeturi Dashboard</p>
            <p className="text-[10px] text-muted-foreground mb-3">Trage pentru reordonare. Click pentru ascundere.</p>
            {widgets.map(wId => (
              <div key={wId} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-white/5 cursor-pointer">
                <GripVertical className="w-3 h-3 text-muted-foreground" />
                <span className="text-xs flex-1">{widgetNames[wId] || wId}</span>
                <button onClick={() => toggleWidget(wId)} className="p-0.5">
                  {hiddenWidgets.includes(wId) ? <EyeOff className="w-3 h-3 text-muted-foreground" /> : <Eye className="w-3 h-3 text-emerald-400" />}
                </button>
              </div>
            ))}
            <Button size="sm" variant="ghost" className="w-full mt-2 text-xs" onClick={() => { setWidgets(defaultWidgets[userType]); setHiddenWidgets([]); toast.success('Dashboard resetat'); }}>
              Reseteaza
            </Button>
          </PopoverContent>
        </Popover>
      </div>

      {/* Development notice */}
      <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/[0.07] p-4" data-testid="dev-notice-banner">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
            <Settings2 className="w-4 h-4 text-amber-400" />
          </div>
          <div className="text-sm">
            <p className="font-semibold text-amber-300" style={{ fontFamily: 'Outfit' }}>Platformă în curs de dezvoltare</p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Această platformă se află în prezent într-o etapă activă de dezvoltare și optimizare.
              Funcționalitățile, interfața și componentele tehnice sunt în curs de implementare, testare și validare.
              Anumite module pot fi temporar indisponibile sau pot suferi modificări fără notificare prealabilă.
              Versiunea disponibilă în prezent nu reprezintă versiunea finală a platformei și este destinată exclusiv activităților de dezvoltare, testare și evaluare.
            </p>
            <p className="text-xs text-amber-300/80 mt-2">Vă mulțumim pentru înțelegere.</p>
          </div>
        </div>
      </div>


      {/* Draggable Widgets */}
      <div className="space-y-6">
        {visibleWidgets.map(wId => {
          const component = widgetComponents[wId];
          if (!component) return null;

          // Stats don't need drag wrapper since they're always full-width
          if (wId === 'stats') {
            return <div key={wId} className="mb-2">{component}</div>;
          }

          // Charts row
          if (['scores', 'procTypes', 'trends'].includes(wId)) {
            const chartWidgets = visibleWidgets.filter(w => ['scores', 'procTypes', 'trends'].includes(w));
            if (chartWidgets[0] !== wId) return null; // Only render once for the first chart
            return (
              <div key="charts-row" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {chartWidgets.map(cId => (
                  <div key={cId}
                    draggable onDragStart={e => handleDragStart(e, cId)} onDragOver={handleDragOver}
                    onDrop={e => handleDrop(e, cId)}
                    className={`cursor-grab active:cursor-grabbing ${draggedWidget === cId ? 'opacity-50' : ''}`}>
                    {widgetComponents[cId]}
                  </div>
                ))}
              </div>
            );
          }

          // Bottom row
          if (['deadlines', 'projects'].includes(wId)) {
            const bottomWidgets = visibleWidgets.filter(w => ['deadlines', 'projects'].includes(w));
            if (bottomWidgets[0] !== wId) return null;
            return (
              <div key="bottom-row" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {bottomWidgets.map(bId => (
                  <div key={bId}
                    draggable onDragStart={e => handleDragStart(e, bId)} onDragOver={handleDragOver}
                    onDrop={e => handleDrop(e, bId)}
                    className={`cursor-grab active:cursor-grabbing ${draggedWidget === bId ? 'opacity-50' : ''}`}>
                    {widgetComponents[bId]}
                  </div>
                ))}
              </div>
            );
          }

          return (
            <div key={wId}
              draggable onDragStart={e => handleDragStart(e, wId)} onDragOver={handleDragOver}
              onDrop={e => handleDrop(e, wId)}
              className={`cursor-grab active:cursor-grabbing ${draggedWidget === wId ? 'opacity-50' : ''}`}>
              {component}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon, color }) {
  const Icon = STAT_ICONS[icon] || FolderKanban;
  return (
    <Card className="bg-card border-white/10 hover:border-white/20 transition-all duration-200">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-bold mt-1.5 font-mono-data">{value}</p>
            {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <Icon className={`w-7 h-7 ${color} opacity-40`} strokeWidth={1.5} />
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }) {
  const styles = { activ: 'bg-blue-500/10 text-blue-400', castigat: 'bg-emerald-500/10 text-emerald-400', pierdut: 'bg-rose-500/10 text-rose-400', finalizat: 'bg-zinc-500/10 text-zinc-400' };
  const labels = { activ: 'Activ', castigat: 'Castigat', pierdut: 'Pierdut', finalizat: 'Finalizat' };
  return <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${styles[status] || styles.activ}`}>{labels[status] || status}</span>;
}
